<template>
  <AuthenticatedView
    :loading="userLoading && !userResult"
    :current-user="currentUser"
    :has-result="!!userResult"
  >
    <div>
      <AppHeader
        :user="currentUser"
        :dashboard-url="dashboardUrl"
        :profile-url="profileUrl"
        :chat-id="chatId"
        :from-id="fromId"
      />

      <div :class="['card', { 'full-width': isFullWidth }]">
        <breadcrumbs :items="breadcrumbsItems" />
        <h2 class="dashboard-page-title">{{ s__('Profile|User Profile') }}</h2>

        <div v-if="loading" class="muted">{{ __('Loading…') }}</div>
        <div v-else-if="error" class="error">{{ error }}</div>
        <div v-else-if="user" class="dashboard">
          <button
            v-if="isMobileLayout"
            type="button"
            class="btn-link-secondary mobile-settings-nav-toggle"
            @click="toggleMobileSidebar"
          >
            {{
              isMobileSidebarExpanded
                ? s__('Profile|Hide settings menu')
                : s__('Profile|Show settings menu')
            }}
          </button>

          <div
            class="dashboard-layout"
            :class="{ 'is-sidebar-collapsed': isSidebarCollapsed && canCollapseSidebar }"
            :style="{ '--dashboard-sticky-top': '12px' }"
          >
            <SettingsSidebarNav
              v-show="!isMobileLayout || isMobileSidebarExpanded"
              :items="profileSidebarItems"
              :active-panel="activeSettingsSection"
              :collapsed="isSidebarCollapsed && canCollapseSidebar"
              :can-collapse="canCollapseSidebar"
              :is-full-width="isFullWidth"
              :can-toggle-full-width="canToggleFullWidth"
              @toggle-panel="openProfileSection"
              @toggle-collapse="toggleSidebarCollapse"
              @toggle-full-width="toggleFullWidth"
            />

            <section class="dashboard-panel">
              <header class="dashboard-panel-header">
                <div>
                  <h3 class="dashboard-panel-title">{{ s__('Profile|Settings') }}</h3>
                  <p class="dashboard-panel-subtitle">{{ activeProfilePanelTitle }}</p>
                </div>
                <div v-if="showHeaderCreateAction" class="header-actions">
                  <button
                    type="button"
                    class="btn-link dashboard-create-task-btn dashboard-header-create-btn"
                    :aria-label="headerCreateActionLabel"
                    @click="handleHeaderCreateAction"
                  >
                    <span class="dashboard-create-task-label">{{ headerCreateActionLabel }}</span>
                    <span class="dashboard-create-task-plus" aria-hidden="true">+</span>
                  </button>
                </div>
              </header>

              <div class="dashboard-panel-body settings-page-content">
                <div class="form">
                  <!-- User Info -->
                  <div
                    v-show="activeSettingsSection === 'account'"
                    class="form-section settings-block"
                    data-settings-section="account"
                  >
                    <div class="section-content">
                      <AlertBanner
                        v-if="accountAlert"
                        :type="accountAlert.type"
                        :title="accountAlert.title"
                        :message="accountAlert.message"
                        @dismiss="accountAlert = null"
                      />
                      <div class="form-row">
                        <label style="display: flex; align-items: center; gap: 8px">
                          {{ s__('Profile|Full name') }}
                          <button
                            class="btn-icon profile-name-edit-btn"
                            :title="s__('Profile|Edit name')"
                            @click="startEditName"
                          >
                            <EditIcon />
                          </button>
                        </label>
                        <div v-if="!editingName" class="muted">
                          {{ user?.name || '—' }}
                        </div>
                        <div v-else>
                          <input
                            v-model="nameForm.name"
                            type="text"
                            :placeholder="s__('Profile|Enter your full name')"
                            style="width: 100%; margin-bottom: 8px"
                          />
                          <div class="mobile-stretch-btn-group" style="display: flex; gap: 8px">
                            <button
                              type="button"
                              class="btn-primary"
                              :disabled="savingName"
                              @click="saveName"
                            >
                              {{ savingName ? s__('Profile|Saving...') : s__('Profile|Save') }}
                            </button>
                            <button
                              type="button"
                              class="btn-secondary"
                              :disabled="savingName"
                              @click="cancelEditName"
                            >
                              {{ __('Cancel') }}
                            </button>
                          </div>
                          <div v-if="nameError" class="error" style="margin-top: 8px">
                            {{ nameError }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="form-row">
                      <label style="display: flex; align-items: center; gap: 8px">
                        {{ s__('Profile|Username') }}
                        <button
                          class="btn-icon profile-name-edit-btn"
                          :title="s__('Profile|Edit username')"
                          @click="startEditUsername"
                        >
                          <EditIcon />
                        </button>
                      </label>
                      <div v-if="!editingUsername" class="muted">
                        {{ user?.username || '—' }}
                      </div>
                      <div v-else>
                        <input
                          v-model="usernameForm.username"
                          type="text"
                          style="width: 100%; margin-bottom: 8px"
                        />
                        <div class="mobile-stretch-btn-group" style="display: flex; gap: 8px">
                          <button
                            type="button"
                            class="btn-primary"
                            :disabled="savingUsername"
                            @click="saveUsername"
                          >
                            {{ savingUsername ? s__('Profile|Saving...') : s__('Profile|Save') }}
                          </button>
                          <button
                            type="button"
                            class="btn-secondary"
                            :disabled="savingUsername"
                            @click="cancelEditUsername"
                          >
                            {{ __('Cancel') }}
                          </button>
                        </div>
                        <div v-if="usernameError" class="error" style="margin-top: 8px">
                          {{ usernameError }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    v-show="activeSettingsSection === 'account'"
                    class="form-section settings-block"
                    data-settings-section="interface"
                  >
                    <div v-show="activeSettingsSection === 'account'" class="section-content">
                      <AlertBanner
                        v-if="interfaceAlert"
                        :type="interfaceAlert.type"
                        :title="interfaceAlert.title"
                        :message="interfaceAlert.message"
                        @dismiss="interfaceAlert = null"
                      />
                      <div class="form-row">
                        <label>{{ s__('Profile|Language') }}</label>
                        <select
                          v-model="form.preferredLanguage"
                          :disabled="saving || autoSaving"
                          class="language-select"
                        >
                          <option value="en">English</option>
                          <option value="ru">Русский</option>
                        </select>
                        <div class="help-text" style="margin-left: 0">
                          {{
                            s__(
                              'Profile|Choose your preferred language or let the app detect it from your Telegram settings'
                            )
                          }}
                        </div>
                      </div>

                      <div class="form-row">
                        <label>{{ s__('Profile|Timezone') }}</label>
                        <select
                          v-model="form.preferredTimezone"
                          :disabled="saving || loadingTimezones || autoSaving"
                          class="timezone-select"
                        >
                          <option v-for="tz in timezones" :key="tz.value" :value="tz.value">
                            {{ tz.label }}
                          </option>
                        </select>
                        <div class="help-text" style="margin-left: 0">
                          {{
                            s__(
                              'Profile|Choose your preferred timezone for displaying dates and times'
                            )
                          }}
                        </div>
                      </div>

                      <div class="form-row">
                        <label>{{ s__('Profile|Emails') }}</label>
                        <div class="profile-emails-section">
                          <div
                            v-for="email in sortedEmails"
                            :key="email.id"
                            class="profile-email-row"
                          >
                            <div class="profile-email-main">
                              <div class="profile-email-address">{{ email.email }}</div>
                              <div class="profile-email-meta">
                                <span v-if="email.primary" class="profile-email-badge primary">
                                  {{ s__('Profile|Primary') }}
                                </span>
                                <span v-if="email.confirmed" class="profile-email-badge confirmed">
                                  {{ s__('Profile|Confirmed') }}
                                </span>
                                <span v-else class="profile-email-badge unconfirmed">
                                  {{ s__('Profile|Not confirmed') }}
                                </span>
                              </div>
                            </div>
                            <div class="profile-email-actions">
                              <button
                                v-if="!email.primary && email.confirmed"
                                class="btn-secondary btn-sm"
                                type="button"
                                :disabled="emailActionLoading"
                                @click="setPrimaryEmail(email.id)"
                              >
                                {{ s__('Profile|Make primary') }}
                              </button>
                              <button
                                v-if="!email.confirmed"
                                class="btn-secondary btn-sm"
                                type="button"
                                :disabled="emailActionLoading"
                                @click="resendEmailConfirmation(email.id)"
                              >
                                {{ s__('Profile|Resend confirmation') }}
                              </button>
                              <button
                                class="btn-secondary btn-sm"
                                type="button"
                                :disabled="emailActionLoading || !canDeleteEmail(email)"
                                @click="confirmDeleteEmail(email)"
                              >
                                {{ s__('Profile|Delete') }}
                              </button>
                            </div>
                          </div>
                          <div class="profile-email-add-row">
                            <input
                              v-model.trim="newEmailForm.email"
                              type="email"
                              :placeholder="s__('Profile|New email address')"
                              :disabled="emailActionLoading || sortedEmails.length >= 3"
                              class="profile-email-input"
                            />
                            <button
                              class="btn-primary btn-sm profile-email-add-button"
                              type="button"
                              :disabled="emailActionLoading || sortedEmails.length >= 3"
                              @click="addEmail"
                            >
                              <PlusIcon :size="16" />
                              <span class="btn-text">{{ s__('Profile|Add email') }}</span>
                            </button>
                          </div>
                          <div class="help-text" style="margin-left: 0">
                            {{ s__('Profile|Maximum 3 emails. New emails require confirmation.') }}
                          </div>
                        </div>
                      </div>

                      <div class="form-row">
                        <label>{{ s__('Profile|Telegram account') }}</label>
                        <div class="profile-telegram-link-section">
                          <div v-if="user?.telegramUserId" class="profile-telegram-link-status">
                            <span class="muted">{{
                              s__('Profile|Telegram account is linked')
                            }}</span>
                            <span class="profile-telegram-badge-id">
                              {{ s__('Profile|Telegram ID') }}: {{ user.telegramUserId }}
                            </span>
                            <button
                              v-if="hasAnyLinkedEmail"
                              type="button"
                              class="btn-danger btn-sm"
                              :disabled="telegramLinkLoading || unlinkTelegramLoading"
                              @click="showUnlinkTelegramConfirm = true"
                            >
                              {{
                                unlinkTelegramLoading
                                  ? s__('Profile|Unlinking...')
                                  : s__('Profile|Unlink Telegram account')
                              }}
                            </button>
                          </div>
                          <template v-else>
                            <div class="settings-toggle-with-info profile-telegram-link-action">
                              <button
                                type="button"
                                class="btn-primary btn-sm"
                                :disabled="telegramLinkLoading || !telegramLinkAvailable"
                                @click="startTelegramLinking"
                              >
                                {{
                                  telegramLinkLoading
                                    ? s__('Profile|Generating link...')
                                    : s__('Profile|Link Telegram account')
                                }}
                              </button>
                              <button
                                type="button"
                                class="info-icon-button"
                                @click.stop="showTelegramLinkInfoModal = true"
                                :title="s__('Profile|Telegram account linking information')"
                                aria-label="Telegram account linking information"
                              >
                                <InfoIcon />
                              </button>
                            </div>
                            <div class="help-text" style="margin-left: 0">
                              {{
                                s__(
                                  'Profile|Click the button, open the bot in Telegram, then confirm linking in the Mini App'
                                )
                              }}
                            </div>
                            <div
                              v-if="!telegramLinkAvailable"
                              class="help-text"
                              style="margin-left: 0"
                            >
                              {{ s__('Profile|Telegram bot is not configured yet') }}
                            </div>
                          </template>
                        </div>
                      </div>

                      <h4 class="feature-flags-block-title">{{ s__('Profile|Features') }}</h4>
                      <div class="feature-flags-mobile-list">
                        <div class="form-row">
                          <div class="compact-task-form-row settings-toggle-with-info">
                            <label class="checkbox-label">
                              <input
                                v-model="form.compactTaskForm"
                                type="checkbox"
                                :disabled="saving || autoSaving"
                              />
                              <span>{{
                                s__('Profile|Use compact task form for creating tasks')
                              }}</span>
                            </label>
                            <button
                              type="button"
                              class="info-icon-button"
                              @click.stop="showCompactFormInfoModal = true"
                              :title="s__('Profile|Compact Task Form Information')"
                              aria-label="Compact Task Form Information"
                            >
                              <InfoIcon />
                            </button>
                          </div>
                          <div class="help-text">
                            {{
                              s__(
                                'Profile|Show only essential fields (Title, Assignee, Due date) when creating tasks'
                              )
                            }}
                          </div>
                        </div>

                        <div class="form-row">
                          <div class="settings-toggle-with-info">
                            <label class="checkbox-label">
                              <input
                                v-model="form.personalLabelsEnabled"
                                type="checkbox"
                                :disabled="saving || autoSaving"
                              />
                              <span>{{ s__('Profile|Enable personal labels') }}</span>
                            </label>
                            <button
                              type="button"
                              class="info-icon-button"
                              @click.stop="showPersonalLabelsInfoModal = true"
                              :title="s__('Profile|Personal Labels Information')"
                              aria-label="Personal Labels Information"
                            >
                              <InfoIcon />
                            </button>
                          </div>
                          <div class="help-text">
                            {{
                              s__(
                                'Profile|Enable personal labels feature to organize tasks across projects with your own labels'
                              )
                            }}
                          </div>
                        </div>

                        <div class="form-row">
                          <div class="settings-toggle-with-info">
                            <label class="checkbox-label">
                              <input
                                v-model="form.boardsEnabled"
                                type="checkbox"
                                :disabled="saving || autoSaving"
                              />
                              <span>{{ s__('Profile|Enable Boards') }}</span>
                            </label>
                            <button
                              type="button"
                              class="info-icon-button"
                              @click.stop="showBoardsInfoModal = true"
                              :title="s__('Profile|Boards Feature Information')"
                              aria-label="Boards Feature Information"
                            >
                              <InfoIcon />
                            </button>
                          </div>
                          <div class="help-text">
                            {{
                              s__('Profile|Allow creating and using boards for visualizing tasks')
                            }}
                          </div>
                        </div>

                        <div class="form-row">
                          <div class="settings-toggle-with-info">
                            <label class="checkbox-label">
                              <input
                                v-model="form.templatesEnabled"
                                type="checkbox"
                                :disabled="saving || autoSaving"
                              />
                              <span>{{ s__('Profile|Enable Task Templates') }}</span>
                            </label>
                            <button
                              type="button"
                              class="info-icon-button"
                              @click.stop="showTemplatesInfoModal = true"
                              :title="s__('Profile|Task Templates Feature Information')"
                              aria-label="Task Templates Feature Information"
                            >
                              <InfoIcon />
                            </button>
                          </div>
                          <div class="help-text">
                            {{ s__('Profile|Allow creating and using personal task templates') }}
                          </div>
                        </div>

                        <div class="form-row">
                          <div class="settings-toggle-with-info">
                            <label class="checkbox-label">
                              <input
                                v-model="form.telegramTaskCommandsEnabled"
                                type="checkbox"
                                :disabled="saving || autoSaving"
                              />
                              <span>{{
                                s__('Profile|Include quick command with task ID in reports')
                              }}</span>
                            </label>
                            <button
                              type="button"
                              class="info-icon-button"
                              @click.stop="showTaskCommandsInfoModal = true"
                              :title="s__('Profile|Task Quick Commands Information')"
                              aria-label="Task Quick Commands Information"
                            >
                              <InfoIcon />
                            </button>
                          </div>
                          <div class="help-text">
                            {{
                              s__(
                                'Profile|Clicking on that quick command (e.g., /i123_456) will open task details and show available quick actions'
                              )
                            }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    v-show="activeSettingsSection === 'notifications'"
                    class="form-section settings-block"
                    data-settings-section="notifications"
                  >
                    <div v-show="expandedSections.notifications" class="section-content">
                      <AlertBanner
                        v-if="notificationsAlert"
                        :type="notificationsAlert.type"
                        :title="notificationsAlert.title"
                        :message="notificationsAlert.message"
                        @dismiss="notificationsAlert = null"
                      />
                      <div class="feature-flags-mobile-list">
                        <div class="form-row">
                          <div class="settings-toggle-with-info">
                            <label class="checkbox-label">
                              <input
                                v-model="form.emailNotificationsEnabled"
                                type="checkbox"
                                :disabled="
                                  saving ||
                                  autoSavingNotifications ||
                                  !hasRelatedEmailsForNotifications
                                "
                              />
                              <span>{{ s__('Profile|Enable email notifications') }}</span>
                            </label>
                            <button
                              type="button"
                              class="info-icon-button"
                              @click.stop="showEmailNotificationsInfoModal = true"
                              :title="s__('Profile|Email Notifications Information')"
                              aria-label="Email Notifications Information"
                            >
                              <InfoIcon />
                            </button>
                          </div>
                          <div class="help-text">
                            {{
                              s__(
                                'Profile|Receive notification emails for task and calendar updates (silent hours do not apply)'
                              )
                            }}
                          </div>
                        </div>

                        <div class="form-row">
                          <div class="settings-toggle-with-info">
                            <label class="checkbox-label">
                              <input
                                v-model="form.dmNotificationsEnabled"
                                type="checkbox"
                                :disabled="
                                  saving ||
                                  autoSavingNotifications ||
                                  !hasTelegramAccountForNotifications
                                "
                              />
                              <span>{{ s__('Profile|Enable Telegram DM notifications') }}</span>
                            </label>
                            <button
                              type="button"
                              class="info-icon-button"
                              @click.stop="showTelegramNotificationsInfoModal = true"
                              :title="s__('Profile|Telegram Notifications Information')"
                              aria-label="Telegram Notifications Information"
                            >
                              <InfoIcon />
                            </button>
                          </div>
                          <div class="help-text">
                            {{
                              s__(
                                'Profile|Receive direct messages from the bot when tasks are assigned or updated'
                              )
                            }}
                          </div>
                        </div>

                        <div class="form-row">
                          <div class="settings-toggle-with-info">
                            <label class="checkbox-label">
                              <input
                                v-model="form.notifyApproachingDeadline"
                                type="checkbox"
                                :disabled="
                                  saving ||
                                  autoSavingNotifications ||
                                  !hasDeadlineNotificationChannel
                                "
                              />
                              <span>{{
                                s__('Profile|Notify about approaching task deadlines')
                              }}</span>
                            </label>
                          </div>
                          <div class="help-text">
                            {{
                              s__(
                                'Profile|Receive notifications 24 hours before task deadlines in enabled channels'
                              )
                            }}
                          </div>
                        </div>

                        <div class="form-row">
                          <div class="settings-toggle-with-info">
                            <label class="checkbox-label">
                              <input
                                v-model="form.notifyOwnActions"
                                type="checkbox"
                                :disabled="saving || autoSavingNotifications"
                              />
                              <span>{{ s__('Profile|Notify me about my own actions') }}</span>
                            </label>
                          </div>
                          <div class="help-text">
                            {{
                              s__(
                                'Profile|Receive notifications when you create or update tasks yourself'
                              )
                            }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="hasTelegramAccountForNotifications"
                    v-show="activeSettingsSection === 'deadlineReports'"
                    data-settings-section="deadlineReports"
                  >
                    <RegularReportsPanel
                      ref="regularReportsPanelRef"
                      :hour="form.deadlineReportHour"
                      :minute="form.deadlineReportMinute"
                      :days="form.deadlineReportDays"
                      :disabled="saving"
                      :saving="savingDeadlineReport"
                      :preferred-language="form.preferredLanguage"
                      @save="handleSaveDeadlineReport"
                      @clear="clearDeadlineReport"
                    />
                  </div>

                  <div v-show="activeSettingsSection === 'notifications'">
                    <UserNotificationPreferences
                      ref="userNotificationPreferencesRef"
                      :notification-preferences="notificationPreferences"
                      :disabled="saving"
                      :dm-notifications-enabled="form.dmNotificationsEnabled"
                      :user-id="userId"
                      @update:changes="handleNotificationPreferenceChanges"
                      @saved="handleNotificationPreferencesSaved"
                      @alert="handleNotificationPreferencesAlert"
                    />
                    <!-- Alert banner for notification preferences - handle via event -->
                    <AlertBanner
                      v-if="notificationPreferencesAlert"
                      :type="notificationPreferencesAlert.type"
                      :title="notificationPreferencesAlert.title"
                      :message="notificationPreferencesAlert.message"
                      @dismiss="notificationPreferencesAlert = null"
                    />
                    <div v-if="hasTelegramAccountForNotifications" class="form-row">
                      <label style="display: flex; align-items: center; gap: 8px">
                        {{ s__('Profile|Silent hours (Telegram DM only)') }}
                        <button
                          type="button"
                          class="info-icon-button"
                          @click="showSilentHoursInfoModal = true"
                          :title="s__('Profile|Silent Hours Information')"
                          aria-label="Silent Hours Information"
                        >
                          <InfoIcon />
                        </button>
                      </label>
                      <SilentHoursForm
                        v-model:start-hour="form.silentHoursStartHour"
                        v-model:start-minute="form.silentHoursStartMinute"
                        v-model:end-hour="form.silentHoursEndHour"
                        v-model:end-minute="form.silentHoursEndMinute"
                        :inputs-disabled="silentHoursDisabled || savingSilentHours"
                        :clear-disabled="saving || !hasSilentHours"
                        :save-disabled="saving || savingSilentHours || !hasSilentHoursChanges"
                        :saving="savingSilentHours"
                        :start-label="s__('Profile|Start')"
                        :end-label="s__('Profile|End')"
                        :clear-text="__('Clear')"
                        :save-text="s__('Profile|Save')"
                        :saving-text="s__('Profile|Saving...')"
                        @clear="clearSilentHours"
                        @save="saveSilentHours"
                      />
                      <div v-if="!hasTimezone" class="help-text" style="color: #d97706">
                        {{
                          s__(
                            'Profile|Please set your preferred timezone above to configure silent hours'
                          )
                        }}
                      </div>
                      <div v-else class="help-text">
                        {{
                          s__(
                            'Profile|Do not send Telegram DM notifications during these hours (times are in your preferred timezone). Email notifications are not affected.'
                          )
                        }}
                      </div>
                    </div>
                  </div>

                  <!-- Personal Labels Management -->
                  <div
                    v-if="form.personalLabelsEnabled"
                    v-show="activeSettingsSection === 'personalLabels'"
                    class="form-section settings-block"
                    data-settings-section="personalLabels"
                  >
                    <div v-show="expandedSections.personalLabels" class="section-content">
                      <LabelsManager
                        ref="labelsManagerRef"
                        scope="user"
                        :expanded="expandedSections.personalLabels"
                        :disabled="saving"
                        :show-create-button="false"
                        :empty-message="s__('Profile|No personal labels defined yet.')"
                        :add-button-text="s__('Profile|Add Personal Label')"
                      />
                    </div>
                  </div>

                  <!-- Custom Report Configuration -->
                  <div
                    v-if="hasTelegramAccountForNotifications"
                    v-show="activeSettingsSection === 'customReport'"
                    class="form-section settings-block"
                    data-settings-section="customReport"
                  >
                    <div class="section-content" v-show="expandedSections.customReport">
                      <ReportConfigurationPanel
                        context="user"
                        :user-id="userId"
                        :chat-id="chatId"
                        :from-id="fromId"
                        :is-personal-project="true"
                        :personal-labels-enabled="form.personalLabelsEnabled"
                        :show-task-type-filter="true"
                        :hide-by-project-option="false"
                        :title="''"
                      />
                    </div>
                  </div>

                  <!-- Task Templates Management -->
                  <div
                    v-if="form.templatesEnabled"
                    v-show="activeSettingsSection === 'taskTemplates'"
                    class="form-section settings-block"
                    data-settings-section="taskTemplates"
                  >
                    <div v-show="expandedSections.taskTemplates" class="section-content">
                      <TaskTemplatesManager
                        ref="taskTemplatesManagerRef"
                        scope="user"
                        :user-id="userId"
                        :expanded="expandedSections.taskTemplates"
                        :disabled="saving"
                        :show-create-button="false"
                        :empty-message="s__('Profile|No task templates defined yet.')"
                        :add-button-text="s__('Profile|New Template')"
                      />
                    </div>
                  </div>

                  <div
                    v-show="activeSettingsSection === 'accessTokens'"
                    class="form-section settings-block"
                    data-settings-section="accessTokens"
                  >
                    <div v-show="expandedSections.accessTokens" class="section-content">
                      <PersonalAccessTokensPanel />
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      <!-- Feature Information Modals -->
      <ProfileBoardsInfoModal :visible="showBoardsInfoModal" @close="showBoardsInfoModal = false" />
      <ProfileTemplatesInfoModal
        :visible="showTemplatesInfoModal"
        @close="showTemplatesInfoModal = false"
      />
      <ProfileTaskCommandsInfoModal
        :visible="showTaskCommandsInfoModal"
        @close="showTaskCommandsInfoModal = false"
      />
      <ProfileCompactFormInfoModal
        :visible="showCompactFormInfoModal"
        @close="showCompactFormInfoModal = false"
      />
      <ProfilePersonalLabelsInfoModal
        :visible="showPersonalLabelsInfoModal"
        @close="showPersonalLabelsInfoModal = false"
      />
      <ProfileSilentHoursInfoModal
        :visible="showSilentHoursInfoModal"
        @close="showSilentHoursInfoModal = false"
      />
      <ProfileCustomReportInfoModal
        :visible="showCustomReportInfoModal"
        @close="showCustomReportInfoModal = false"
      />
      <ProfileTelegramNotificationsInfoModal
        :visible="showTelegramNotificationsInfoModal"
        :bot-username="botUsername"
        @close="showTelegramNotificationsInfoModal = false"
      />
      <ProfileEmailNotificationsInfoModal
        :visible="showEmailNotificationsInfoModal"
        @close="showEmailNotificationsInfoModal = false"
      />
      <ProfileTelegramLinkInfoModal
        :visible="showTelegramLinkInfoModal"
        :bot-username="botUsername"
        @close="showTelegramLinkInfoModal = false"
      />
      <ConfirmModal
        :visible="showUnlinkTelegramConfirm"
        :title="s__('Profile|Unlink Telegram account?')"
        :message="
          s__(
            'Profile|After unlinking, Tracktor bot will stop sending direct messages. You can link your Telegram account again anytime.'
          )
        "
        :confirm-text="s__('Profile|Unlink')"
        :cancel-text="__('Cancel')"
        @confirm="confirmUnlinkTelegramAccount"
        @cancel="showUnlinkTelegramConfirm = false"
      />
      <ConfirmModal
        :visible="showEmailNotificationsOptOutConfirm"
        :title="s__('Profile|Turn off email notifications?')"
        :message="
          s__(
            'Profile|We will stop sending notification emails to this account. You can turn them back on anytime in your profile settings.'
          )
        "
        :confirm-text="s__('Profile|Turn off')"
        :cancel-text="__('Cancel')"
        @confirm="confirmEmailNotificationsOptOut"
        @cancel="showEmailNotificationsOptOutConfirm = false"
      />
    </div>
  </AuthenticatedView>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, __, sprintf, loadTranslations } from '@/locale'
import UserNotificationPreferences from './UserNotificationPreferences.vue'
import LabelsManager from '../common/LabelsManager.vue'
import TaskTemplatesManager from '../common/TaskTemplatesManager.vue'
import PersonalAccessTokensPanel from './PersonalAccessTokensPanel.vue'
import SilentHoursForm from '../common/SilentHoursForm.vue'
import ReportConfigurationPanel from './ReportConfigurationPanel.vue'
import RegularReportsPanel from './RegularReportsPanel.vue'
import EditIcon from '../common/icons/EditIcon.vue'
import InfoIcon from '../common/icons/InfoIcon.vue'
import SettingsIcon from '../common/icons/SettingsIcon.vue'
import PlusIcon from '../common/icons/PlusIcon.vue'
import BellPlusIcon from '../common/icons/BellPlusIcon.vue'
import ClockIcon from '../common/icons/ClockIcon.vue'
import TagIcon from '../common/icons/TagIcon.vue'
import DocumentFileIcon from '../common/icons/DocumentFileIcon.vue'
import FileIcon from '../common/icons/FileIcon.vue'
import LockIcon from '../common/icons/LockIcon.vue'
import FaviconMarkIcon from '../common/icons/FaviconMarkIcon.vue'
import AppHeader from '../common/AppHeader.vue'
import AlertBanner from '../common/AlertBanner.vue'
import AuthenticatedView from '../common/AuthenticatedView.vue'
import Breadcrumbs from '../common/Breadcrumbs.vue'
import ProfileBoardsInfoModal from './ProfileBoardsInfoModal.vue'
import ProfileTemplatesInfoModal from './ProfileTemplatesInfoModal.vue'
import '../../styles/settings-toggle-with-info.css'
import ProfileTaskCommandsInfoModal from './ProfileTaskCommandsInfoModal.vue'
import ProfileCompactFormInfoModal from './ProfileCompactFormInfoModal.vue'
import ProfilePersonalLabelsInfoModal from './ProfilePersonalLabelsInfoModal.vue'
import ProfileSilentHoursInfoModal from './ProfileSilentHoursInfoModal.vue'
import ProfileCustomReportInfoModal from './ProfileCustomReportInfoModal.vue'
import ProfileTelegramNotificationsInfoModal from './ProfileTelegramNotificationsInfoModal.vue'
import ProfileEmailNotificationsInfoModal from './ProfileEmailNotificationsInfoModal.vue'
import ProfileTelegramLinkInfoModal from './ProfileTelegramLinkInfoModal.vue'
import SettingsSidebarNav from '../common/SettingsSidebarNav.vue'
import ConfirmModal from '../common/ConfirmModal.vue'
import { removeInitialLoading } from '@/utils/loadingScreen'
import { readFullWidthPreference, writeFullWidthPreference } from '@/utils/fullWidthPreference'
import {
  readSidebarCollapsedPreference,
  writeSidebarCollapsedPreference,
} from '@/utils/sidebarCollapsePreference'

const props = defineProps({ chatId: String, fromId: String })
const PROFILE_SETTINGS_ACTIVE_SECTION_SESSION_KEY = 'profileSettingsActiveSection'
const PROFILE_SETTINGS_SECTION_QUERY = 'settings_section'
const PROFILE_EMAIL_OPT_OUT_QUERY = 'email_notifications_opt_out'
const PROFILE_EMAIL_OPT_OUT_STATUS_QUERY = 'email_notifications_opt_out_status'
const PROFILE_EMAIL_OPT_OUT_CONFIRM_VALUE = 'confirm'
const PROFILE_EMAIL_OPT_OUT_INVALID_VALUE = 'invalid_token'

// Feature flag for S3 attachments
const s3AttachmentsEnabled = computed(() => {
  return window.gon?.s3_attachments_enabled === true
})

const loading = ref(true)
const error = ref('')
const user = ref(null)
const userId = ref(null)
const saving = ref(false)
const autoSaving = ref(false)
const interfaceAlert = ref(null)
const accountAlert = ref(null)
const autoSavingNotifications = ref(false)
const notificationsAlert = ref(null)
const notificationPreferencesAlert = ref(null)
const savingSilentHours = ref(false)
const savingDeadlineReport = ref(false)
const loadingTimezones = ref(true)
const timezones = ref([])
const detectedTimezone = ref('')
const localeKey = ref(0) // Reactive key to force re-render when translations load
const activeSettingsSection = ref('account')
const pendingSettingsSection = ref(null)
const canCollapseSidebar = ref(false)
const isSidebarCollapsed = ref(false)
const isMobileLayout = ref(false)
const isMobileSidebarExpanded = ref(false)
const isFullWidth = ref(false)
const canToggleFullWidth = ref(false)
const showEmailNotificationsOptOutConfirm = ref(false)
const savingEmailNotificationsOptOut = ref(false)
const pendingEmailNotificationsOptOutAction = ref(null)
const pendingEmailNotificationsOptOutStatus = ref(null)

// Collapsible sections state
const expandedSections = ref({
  interface: true,
  notifications: false,
  deadlineReports: false,
  personalLabels: false,
  taskTemplates: false,
  accessTokens: false,
  telegramCommandsInfo: false,
  customReport: false,
})

const setActiveProfileSection = section => {
  if (!section) return
  const activeChanged = activeSettingsSection.value !== section
  const nextExpanded = { ...expandedSections.value }
  let expandedChanged = false
  Object.keys(nextExpanded).forEach(key => {
    const shouldBeExpanded = key === section
    if (nextExpanded[key] !== shouldBeExpanded) {
      nextExpanded[key] = shouldBeExpanded
      expandedChanged = true
    }
  })

  if (!activeChanged && !expandedChanged) return

  activeSettingsSection.value = section
  if (expandedChanged) {
    expandedSections.value = nextExpanded
  }

  try {
    sessionStorage.setItem(PROFILE_SETTINGS_ACTIVE_SECTION_SESSION_KEY, section)
  } catch {}
}

const profileSidebarItems = computed(() => {
  const items = [
    {
      key: 'account',
      label: s__('Profile|General'),
      icon: SettingsIcon,
      expanded: activeSettingsSection.value === 'account',
    },
    {
      key: 'notifications',
      label: s__('Profile|Notifications'),
      icon: BellPlusIcon,
      expanded: expandedSections.value.notifications,
    },
    {
      key: 'deadlineReports',
      label: s__('Profile|Telegram Regular Reports'),
      icon: ClockIcon,
      expanded: expandedSections.value.deadlineReports,
      hidden: !hasTelegramAccountForNotifications.value,
    },
    {
      key: 'personalLabels',
      label: s__('Profile|My Labels'),
      icon: TagIcon,
      expanded: expandedSections.value.personalLabels,
      hidden: !form.value.personalLabelsEnabled,
    },
    {
      key: 'customReport',
      label: s__('Profile|My Custom Report'),
      icon: DocumentFileIcon,
      expanded: expandedSections.value.customReport,
    },
    {
      key: 'taskTemplates',
      label: s__('Profile|Task Templates'),
      icon: FileIcon,
      expanded: expandedSections.value.taskTemplates,
      hidden: !form.value.templatesEnabled,
    },
    {
      key: 'accessTokens',
      label: s__('Profile|Access Tokens'),
      icon: LockIcon,
      expanded: expandedSections.value.accessTokens,
    },
    {
      key: 'go-dashboard',
      type: 'link',
      label: s__('Dashboard|Dashboard'),
      url: dashboardUrl.value,
      icon: FaviconMarkIcon,
    },
  ]

  return items.filter(item => !item.hidden)
})

const activeProfilePanelTitle = computed(() => {
  const activeItem = profileSidebarItems.value.find(
    item => item.type !== 'link' && item.key === activeSettingsSection.value
  )
  return activeItem?.label || s__('Profile|Settings')
})

const showHeaderCreateAction = computed(() => {
  if (activeSettingsSection.value === 'personalLabels') {
    return form.value.personalLabelsEnabled
  }
  if (activeSettingsSection.value === 'taskTemplates') {
    return form.value.templatesEnabled
  }
  return false
})

const headerCreateActionLabel = computed(() => {
  if (activeSettingsSection.value === 'personalLabels') {
    return s__('Profile|Add Personal Label')
  }
  if (activeSettingsSection.value === 'taskTemplates') {
    return s__('Profile|New Template')
  }
  return s__('Dashboard|Create Task')
})

const syncActiveProfileSection = items => {
  const visibleKeys = items.filter(item => item.type !== 'link').map(item => item.key)
  if (visibleKeys.length === 0) return

  if (pendingSettingsSection.value && visibleKeys.includes(pendingSettingsSection.value)) {
    setActiveProfileSection(pendingSettingsSection.value)
    pendingSettingsSection.value = null
    return
  }

  if (!visibleKeys.includes(activeSettingsSection.value)) {
    setActiveProfileSection(visibleKeys[0])
  }
}

const scrollToProfileSection = async section => {
  if (typeof window === 'undefined' || window.innerWidth >= 1024) return

  await nextTick()

  const sectionElement = document.querySelector(`[data-settings-section="${section}"]`)
  if (!(sectionElement instanceof HTMLElement)) return
  const panelElement = sectionElement.closest('.dashboard-panel')
  const panelHeaderElement = panelElement?.querySelector('.dashboard-panel-header')
  const targetElement =
    panelHeaderElement instanceof HTMLElement ? panelHeaderElement : sectionElement

  let topOffset = 12
  const headerElement = document.querySelector('.app-header')
  if (headerElement instanceof HTMLElement) {
    const headerStyles = window.getComputedStyle(headerElement)
    if (headerStyles.position === 'sticky') {
      const top = parseFloat(headerStyles.top || '0') || 0
      topOffset = Math.ceil(headerElement.offsetHeight + top + 12)
    }
  }

  const targetTop = targetElement.getBoundingClientRect().top + window.scrollY - topOffset
  window.scrollTo({ top: Math.max(targetTop, 0), behavior: 'smooth' })
}

const openProfileSection = section => {
  setActiveProfileSection(section)
  if (isMobileLayout.value) {
    isMobileSidebarExpanded.value = false
  }
  scrollToProfileSection(section)
}

const toggleMobileSidebar = () => {
  if (!isMobileLayout.value) return
  isMobileSidebarExpanded.value = !isMobileSidebarExpanded.value
}

const handleHeaderCreateAction = () => {
  if (activeSettingsSection.value === 'personalLabels') {
    labelsManagerRef.value?.openCreateForm?.()
    return
  }
  if (activeSettingsSection.value === 'taskTemplates') {
    taskTemplatesManagerRef.value?.openCreateModal?.()
  }
}

const toggleSidebarCollapse = () => {
  if (!canCollapseSidebar.value) return
  isSidebarCollapsed.value = !isSidebarCollapsed.value
  writeSidebarCollapsedPreference(isSidebarCollapsed.value)
}

const toggleFullWidth = () => {
  if (!canToggleFullWidth.value) return
  isFullWidth.value = !isFullWidth.value
  writeFullWidthPreference(isFullWidth.value)
}

const checkSettingsLayoutCapabilities = () => {
  if (typeof window === 'undefined') return

  canCollapseSidebar.value = window.innerWidth >= 1024
  isMobileLayout.value = window.innerWidth < 1024
  canToggleFullWidth.value = window.innerWidth >= 1024

  if (!canCollapseSidebar.value) {
    isSidebarCollapsed.value = false
  }
  if (!isMobileLayout.value) {
    isMobileSidebarExpanded.value = false
  }
  isFullWidth.value = canToggleFullWidth.value && readFullWidthPreference()
}

// Reference to UserNotificationPreferences component
const userNotificationPreferencesRef = ref(null)
// Reference to RegularReportsPanel component
const regularReportsPanelRef = ref(null)
// References to manager components for header actions
const labelsManagerRef = ref(null)
const taskTemplatesManagerRef = ref(null)

// Feature info modal state
const showBoardsInfoModal = ref(false)
const showTemplatesInfoModal = ref(false)
const showTaskCommandsInfoModal = ref(false)
const showCompactFormInfoModal = ref(false)
const showPersonalLabelsInfoModal = ref(false)
const showSilentHoursInfoModal = ref(false)
const showCustomReportInfoModal = ref(false)
const showTelegramNotificationsInfoModal = ref(false)
const showEmailNotificationsInfoModal = ref(false)
const showTelegramLinkInfoModal = ref(false)
const showUnlinkTelegramConfirm = ref(false)

const form = ref({
  dmNotificationsEnabled: true,
  emailNotificationsEnabled: false,
  notifyOwnActions: false,
  notifyApproachingDeadline: true,
  deadlineReportHour: null,
  deadlineReportMinute: null,
  deadlineReportDays: [],
  silentHoursStartHour: null,
  silentHoursStartMinute: null,
  silentHoursEndHour: null,
  silentHoursEndMinute: null,
  compactTaskForm: false,
  preferredLanguage: null,
  preferredTimezone: null,
  personalLabelsEnabled: false,
  boardsEnabled: true,
  templatesEnabled: false,
  telegramTaskCommandsEnabled: false,
})

// Notification preferences state
const notificationPreferences = ref({})

const originalForm = ref({
  dmNotificationsEnabled: true,
  emailNotificationsEnabled: false,
  notifyOwnActions: false,
  notifyApproachingDeadline: true,
  deadlineReportHour: null,
  deadlineReportMinute: null,
  deadlineReportDays: [],
  silentHoursStartHour: null,
  silentHoursStartMinute: null,
  silentHoursEndHour: null,
  silentHoursEndMinute: null,
  compactTaskForm: false,
  preferredLanguage: null,
  preferredTimezone: null,
  personalLabelsEnabled: false,
  boardsEnabled: true,
  templatesEnabled: false,
  telegramTaskCommandsEnabled: false,
})

// Computed property for week days with translations and conditional ordering
// Russian: Monday first, Sunday last
// Others: Sunday first, Saturday last
// Day values remain consistent: 0=Sunday, 1=Monday, etc.
const weekDays = computed(() => {
  const isRussian = form.value.preferredLanguage === 'ru'

  // Define days with their actual indices (0=Sunday, 1=Monday, etc.)
  const days = [
    { label: s__('Profile|Sun'), value: 0 },
    { label: s__('Profile|Mon'), value: 1 },
    { label: s__('Profile|Tue'), value: 2 },
    { label: s__('Profile|Wed'), value: 3 },
    { label: s__('Profile|Thu'), value: 4 },
    { label: s__('Profile|Fri'), value: 5 },
    { label: s__('Profile|Sat'), value: 6 },
  ]

  if (isRussian) {
    // Russian: Monday first, Sunday last
    return [
      days[1], // Mon (value: 1)
      days[2], // Tue (value: 2)
      days[3], // Wed (value: 3)
      days[4], // Thu (value: 4)
      days[5], // Fri (value: 5)
      days[6], // Sat (value: 6)
      days[0], // Sun (value: 0)
    ]
  } else {
    // Default: Sunday first, Saturday last
    return days
  }
})

// Name editing state (separate from form)
const editingName = ref(false)
const savingName = ref(false)
const nameError = ref('')
const nameForm = ref({
  name: '',
})
const NAME_MIN_LENGTH = 2
const editingUsername = ref(false)
const savingUsername = ref(false)
const usernameError = ref('')
const originalEditingName = ref('')
const originalEditingUsername = ref('')
const usernameForm = ref({
  username: '',
})
const newEmailForm = ref({
  email: '',
})
const emailActionLoading = ref(false)
const telegramLinkLoading = ref(false)
const unlinkTelegramLoading = ref(false)
const sortedEmails = computed(() => {
  const emails = user.value?.emails || []
  return [...emails].sort((a, b) => {
    if (a.primary === b.primary) return 0
    return a.primary ? -1 : 1
  })
})

const hasRelatedEmailsForNotifications = computed(() =>
  sortedEmails.value.some(email => email.primary && email.confirmed && email.notifiable !== false)
)
const hasAnyLinkedEmail = computed(() => sortedEmails.value.length > 0)
const hasTelegramAccountForNotifications = computed(() => !!user.value?.telegramUserId)
const hasDeadlineNotificationChannel = computed(
  () => hasTelegramAccountForNotifications.value || hasRelatedEmailsForNotifications.value
)
const telegramLinkAvailable = computed(() => !!botUsername.value)

// hasChanges is no longer needed - all sections now have auto-save or their own Save buttons

function handleNotificationPreferenceChanges(changes) {
  // This is now handled by auto-save in UserNotificationPreferences component
  // Keep for backwards compatibility but no longer needed
}

function handleNotificationPreferencesSaved(updatedPreferences) {
  // Update notification preferences when auto-saved
  notificationPreferences.value = updatedPreferences || {}
  if (userNotificationPreferencesRef.value) {
    userNotificationPreferencesRef.value.clearAlert()
  }
}

function handleNotificationPreferencesAlert(alertData) {
  notificationPreferencesAlert.value = alertData
  // Auto-clear after 3 seconds for success messages
  if (alertData.type === 'success') {
    setTimeout(() => {
      notificationPreferencesAlert.value = null
    }, 3000)
  }
}

const dashboardUrl = computed(() => {
  let url = '/dashboard'
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
})

const attachmentsUrl = computed(() => {
  let url = '/dashboard?tab=attachments'
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '&' + params.join('&')
  return url
})

const profileUrl = computed(() => {
  let url = '/profile'
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
})

const breadcrumbsItems = computed(() => [
  { label: s__('Dashboard|Main'), url: dashboardUrl.value },
  { label: s__('Profile|User Profile') },
])

const hasSilentHours = computed(() => {
  return !!(
    form.value.silentHoursStartHour ||
    form.value.silentHoursStartMinute ||
    form.value.silentHoursEndHour ||
    form.value.silentHoursEndMinute
  )
})

const hasSilentHoursChanges = computed(() => {
  const normalizeValue = val => (val === null || val === undefined ? null : String(val))
  return (
    normalizeValue(form.value.silentHoursStartHour) !==
      normalizeValue(originalForm.value.silentHoursStartHour) ||
    normalizeValue(form.value.silentHoursStartMinute) !==
      normalizeValue(originalForm.value.silentHoursStartMinute) ||
    normalizeValue(form.value.silentHoursEndHour) !==
      normalizeValue(originalForm.value.silentHoursEndHour) ||
    normalizeValue(form.value.silentHoursEndMinute) !==
      normalizeValue(originalForm.value.silentHoursEndMinute)
  )
})

const hasTimezone = computed(() => {
  // Check if user has explicitly set a timezone (not auto-detect)
  return !!form.value.preferredTimezone
})

const silentHoursDisabled = computed(() => {
  return saving.value || !hasTimezone.value
})

// Get viewer's Telegram user ID
const viewerUid = (() => {
  const tg = window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.initDataUnsafe
  return (tg && tg.user && tg.user.id) || props.fromId || null
})()

// GraphQL queries and mutations
const USER_Q_TELEGRAM = gql`
  query GetUserProfileByTelegram($uid: BigInt!) {
    userByTelegram(telegramUserId: $uid) {
      id
      telegramUserId
      name
      username
      emails {
        id
        email
        primary
        confirmed
        notifiable
      }
      dmNotificationsEnabled
      emailNotificationsEnabled
      notifyOwnActions
      notifyApproachingDeadline
      deadlineReportTime
      deadlineReportDays
      silentHours
      notificationPreferences
      compactTaskForm
      preferredLanguage
      preferredTimezone
      personalLabelsEnabled
      boardsEnabled
      templatesEnabled
      telegramTaskCommandsEnabled
    }
  }
`

const USER_Q_CURRENT = gql`
  query GetCurrentUserProfile {
    currentUser {
      id
      telegramUserId
      name
      username
      emails {
        id
        email
        primary
        confirmed
        notifiable
      }
      dmNotificationsEnabled
      emailNotificationsEnabled
      notifyOwnActions
      notifyApproachingDeadline
      deadlineReportTime
      deadlineReportDays
      silentHours
      notificationPreferences
      compactTaskForm
      preferredLanguage
      preferredTimezone
      personalLabelsEnabled
      boardsEnabled
      templatesEnabled
      telegramTaskCommandsEnabled
    }
  }
`

const TIMEZONES_Q = gql`
  query GetTimezones {
    timezoneOptions {
      value
      label
    }
  }
`

const BOT_USERNAME_Q = gql`
  query GetBotUsername {
    botUsername
  }
`

const UPDATE_USER_NAME_MUTATION = gql`
  mutation UpdateUserName($userId: ID!, $name: String) {
    updateUserName(userId: $userId, name: $name) {
      user {
        id
        name
      }
      errors
    }
  }
`

const UPDATE_USER_USERNAME_MUTATION = gql`
  mutation UpdateUserUsername($userId: ID!, $username: String!) {
    updateUserUsername(userId: $userId, username: $username) {
      user {
        id
        username
      }
      errors
    }
  }
`

const CREATE_USER_EMAIL_MUTATION = gql`
  mutation CreateUserEmail($userId: ID!, $email: String!) {
    createUserEmail(userId: $userId, email: $email) {
      email {
        id
      }
      errors
    }
  }
`

const SET_PRIMARY_USER_EMAIL_MUTATION = gql`
  mutation SetPrimaryUserEmail($userId: ID!, $emailId: ID!) {
    setPrimaryUserEmail(userId: $userId, emailId: $emailId) {
      email {
        id
      }
      errors
    }
  }
`

const DELETE_USER_EMAIL_MUTATION = gql`
  mutation DeleteUserEmail($userId: ID!, $emailId: ID!) {
    deleteUserEmail(userId: $userId, emailId: $emailId) {
      success
      errors
    }
  }
`

const RESEND_USER_EMAIL_CONFIRMATION_MUTATION = gql`
  mutation ResendUserEmailConfirmation($userId: ID!, $emailId: ID!) {
    resendUserEmailConfirmation(userId: $userId, emailId: $emailId) {
      success
      errors
    }
  }
`

const CREATE_TELEGRAM_LINK_TOKEN_MUTATION = gql`
  mutation CreateTelegramLinkToken {
    createTelegramLinkToken {
      success
      linkUrl
      expiresAt
      errors
    }
  }
`

const COMPLETE_TELEGRAM_LINK_MUTATION = gql`
  mutation CompleteTelegramLink($token: String!, $initData: String!) {
    completeTelegramLink(token: $token, initData: $initData) {
      success
      errors
    }
  }
`

const UNLINK_TELEGRAM_ACCOUNT_MUTATION = gql`
  mutation UnlinkTelegramAccount($userId: ID!) {
    unlinkTelegramAccount(userId: $userId) {
      user {
        id
        telegramUserId
      }
      errors
    }
  }
`

const UPDATE_PREFERENCES_MUTATION = gql`
  mutation UpdateUserPreferences(
    $userId: ID!
    $dmNotificationsEnabled: Boolean
    $emailNotificationsEnabled: Boolean
    $notifyOwnActions: Boolean
    $notifyApproachingDeadline: Boolean
    $deadlineReportTime: String
    $deadlineReportDays: [Int!]
    $silentHours: JSON
    $notificationPreferences: JSON
    $compactTaskForm: Boolean
    $preferredLanguage: String
    $preferredTimezone: String
    $personalLabelsEnabled: Boolean
    $boardsEnabled: Boolean
    $templatesEnabled: Boolean
    $telegramTaskCommandsEnabled: Boolean
  ) {
    updateUserPreferences(
      userId: $userId
      dmNotificationsEnabled: $dmNotificationsEnabled
      emailNotificationsEnabled: $emailNotificationsEnabled
      notifyOwnActions: $notifyOwnActions
      notifyApproachingDeadline: $notifyApproachingDeadline
      deadlineReportTime: $deadlineReportTime
      deadlineReportDays: $deadlineReportDays
      silentHours: $silentHours
      notificationPreferences: $notificationPreferences
      compactTaskForm: $compactTaskForm
      preferredLanguage: $preferredLanguage
      preferredTimezone: $preferredTimezone
      personalLabelsEnabled: $personalLabelsEnabled
      boardsEnabled: $boardsEnabled
      templatesEnabled: $templatesEnabled
      telegramTaskCommandsEnabled: $telegramTaskCommandsEnabled
    ) {
      user {
        id
        dmNotificationsEnabled
        emailNotificationsEnabled
        notifyOwnActions
        notifyApproachingDeadline
        deadlineReportTime
        deadlineReportDays
        silentHours
        notificationPreferences
        compactTaskForm
        preferredLanguage
        preferredTimezone
        personalLabelsEnabled
        boardsEnabled
        templatesEnabled
        telegramTaskCommandsEnabled
      }
      errors
    }
  }
`

// Fetch timezones
const { result: timezonesResult, loading: tzLoading } = useQuery(TIMEZONES_Q)

// Fetch bot username
const botUsername = ref(null)
const { result: botUsernameResult } = useQuery(BOT_USERNAME_Q)

watch(
  tzLoading,
  val => {
    loadingTimezones.value = val
  },
  { immediate: true }
)
watch(
  timezonesResult,
  data => {
    if (data?.timezoneOptions) {
      timezones.value = data.timezoneOptions
    }
  },
  { immediate: true }
)
watch(
  botUsernameResult,
  data => {
    if (data?.botUsername) {
      botUsername.value = data.botUsername
    }
  },
  { immediate: true }
)

// Detect browser timezone
if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
  detectedTimezone.value = Intl.DateTimeFormat().resolvedOptions().timeZone
}

// Use currentUser query when there's no Telegram context (browser authentication)
// Otherwise use userByTelegram query
const useCurrentUserQuery = computed(() => !viewerUid)

const {
  result: userResultTelegram,
  loading: userLoadingTelegram,
  error: userErrorTelegram,
  refetch: refetchUserTelegram,
} = useQuery(
  USER_Q_TELEGRAM,
  { uid: Number(viewerUid) },
  {
    enabled: computed(() => !!viewerUid),
    skip: useCurrentUserQuery.value,
    fetchPolicy: 'network-only',
  }
)

const {
  result: userResultCurrent,
  loading: userLoadingCurrent,
  error: userErrorCurrent,
  refetch: refetchUserCurrent,
} = useQuery(
  USER_Q_CURRENT,
  {},
  {
    enabled: useCurrentUserQuery,
    skip: !useCurrentUserQuery.value,
    fetchPolicy: 'network-only',
  }
)

// Combine results - use whichever query is active
const userResult = computed(() =>
  useCurrentUserQuery.value ? userResultCurrent.value : userResultTelegram.value
)
const userLoading = computed(() =>
  useCurrentUserQuery.value ? userLoadingCurrent.value : userLoadingTelegram.value
)
const userError = computed(() =>
  useCurrentUserQuery.value ? userErrorCurrent.value : userErrorTelegram.value
)

// Current user for AppHeader
const currentUser = computed(() => {
  const userData = userResult.value?.userByTelegram || userResult.value?.currentUser
  return userData
})

// Helper function to refetch user data
const refetchUser = async () => {
  if (useCurrentUserQuery.value) {
    await refetchUserCurrent()
  } else {
    await refetchUserTelegram({ uid: Number(viewerUid) })
  }
}

watch(
  userLoading,
  val => {
    loading.value = val
  },
  { immediate: true }
)

// Remove HTML loading screen when data is loaded
watch(loading, isLoading => {
  if (!isLoading) {
    removeInitialLoading()
  }
})

watch(
  userError,
  val => {
    if (val) error.value = val.message || 'Failed to load user'
  },
  { immediate: true }
)
watch(
  userResult,
  data => {
    // Handle both query types: userByTelegram returns { userByTelegram }, currentUser returns { currentUser }
    const userData = data?.userByTelegram || data?.currentUser
    if (userData) {
      user.value = userData
      userId.value = userData.id

      // Convert silent_hours from UTC to user's timezone for display
      const userTz = userData.preferredTimezone || detectedTimezone.value || 'UTC'
      const silentHoursInUserTz = convertSilentHoursFromUtc(userData.silentHours, userTz)

      // Parse time strings into hour and minute components
      const startParts = silentHoursInUserTz.start
        ? silentHoursInUserTz.start.split(':')
        : [null, null]
      const endParts = silentHoursInUserTz.end ? silentHoursInUserTz.end.split(':') : [null, null]

      // Parse deadline report time
      const deadlineReportParts = userData.deadlineReportTime
        ? userData.deadlineReportTime.split(':')
        : [null, null]

      // Default to Europe/Moscow if timezone is not set
      let defaultTimezone = userData.preferredTimezone
      if (!defaultTimezone && timezones.value.length > 0) {
        const moscowTimezone = timezones.value.find(tz => tz.value === 'Europe/Moscow')
        defaultTimezone = moscowTimezone ? 'Europe/Moscow' : timezones.value[0].value
      }

      // Mark that we're populating the form to prevent auto-save from triggering
      isPopulatingForm.value = true

      form.value = {
        dmNotificationsEnabled: userData.dmNotificationsEnabled,
        emailNotificationsEnabled: userData.emailNotificationsEnabled ?? false,
        notifyOwnActions: userData.notifyOwnActions,
        notifyApproachingDeadline: userData.notifyApproachingDeadline ?? true,
        deadlineReportHour: deadlineReportParts[0] || null,
        deadlineReportMinute: deadlineReportParts[1] || null,
        deadlineReportDays: (userData.deadlineReportDays || []).map(d => Number(d)),
        silentHoursStartHour: startParts[0] || null,
        silentHoursStartMinute: startParts[1] || null,
        silentHoursEndHour: endParts[0] || null,
        silentHoursEndMinute: endParts[1] || null,
        compactTaskForm: userData.compactTaskForm,
        preferredLanguage: userData.preferredLanguage || 'en', // Default to 'en' if null
        preferredTimezone: defaultTimezone,
        personalLabelsEnabled: userData.personalLabelsEnabled ?? false,
        boardsEnabled: userData.boardsEnabled ?? true,
        templatesEnabled: userData.templatesEnabled ?? false,
        telegramTaskCommandsEnabled: userData.telegramTaskCommandsEnabled ?? false,
      }
      originalForm.value = { ...form.value }

      // Load translations for the user's preferred language
      if (userData.preferredLanguage) {
        document.documentElement.lang = userData.preferredLanguage
        loadTranslations(userData.preferredLanguage)
        localeKey.value++ // Force re-render of breadcrumbs
      }

      // Load notification preferences
      notificationPreferences.value = userData.notificationPreferences || {}

      // Clear any existing alerts when user data is loaded
      accountAlert.value = null
      interfaceAlert.value = null
      notificationsAlert.value = null
      handlePendingEmailNotificationsOptOut()

      // Allow auto-save now that form is populated (set before formInitialized)
      isPopulatingForm.value = false

      // Use nextTick to ensure form is fully populated before enabling watcher
      // This prevents the watcher from firing during form initialization
      setTimeout(() => {
        // Mark form as initialized after user data is loaded
        formInitialized.value = true
      }, 100)
    }
  },
  { immediate: true }
)

// Auto-set minutes to "00" when hour is selected and minutes is blank
watch(
  () => form.value.silentHoursStartHour,
  newHour => {
    if (newHour && !form.value.silentHoursStartMinute) {
      form.value.silentHoursStartMinute = '00'
    }
  }
)

watch(
  () => form.value.silentHoursEndHour,
  newHour => {
    if (newHour && !form.value.silentHoursEndMinute) {
      form.value.silentHoursEndMinute = '00'
    }
  }
)

watch(
  () => form.value.deadlineReportHour,
  newHour => {
    if (newHour && form.value.deadlineReportMinute === null) {
      form.value.deadlineReportMinute = '00'
    }
  }
)

// Watch for timezone changes and update silent hours display
watch(
  () => form.value.preferredTimezone,
  (newTz, oldTz) => {
    // Only update if we have silent hours and timezone actually changed
    if (user.value?.silentHours && newTz !== oldTz && oldTz !== undefined) {
      const userTz = newTz || detectedTimezone.value || 'UTC'
      const silentHoursInUserTz = convertSilentHoursFromUtc(user.value.silentHours, userTz)

      // Parse time strings into hour and minute components
      const startParts = silentHoursInUserTz.start
        ? silentHoursInUserTz.start.split(':')
        : [null, null]
      const endParts = silentHoursInUserTz.end ? silentHoursInUserTz.end.split(':') : [null, null]

      form.value.silentHoursStartHour = startParts[0] || null
      form.value.silentHoursStartMinute = startParts[1] || null
      form.value.silentHoursEndHour = endParts[0] || null
      form.value.silentHoursEndMinute = endParts[1] || null

      // Update originalForm to keep it in sync after timezone conversion
      originalForm.value.silentHoursStartHour = startParts[0] || null
      originalForm.value.silentHoursStartMinute = startParts[1] || null
      originalForm.value.silentHoursEndHour = endParts[0] || null
      originalForm.value.silentHoursEndMinute = endParts[1] || null
    }
  }
)

// Mutations
const { mutate: updatePreferences } = useMutation(UPDATE_PREFERENCES_MUTATION)
const { mutate: updateUserName } = useMutation(UPDATE_USER_NAME_MUTATION)
const { mutate: updateUserUsername } = useMutation(UPDATE_USER_USERNAME_MUTATION)
const { mutate: createUserEmail } = useMutation(CREATE_USER_EMAIL_MUTATION)
const { mutate: setPrimaryUserEmail } = useMutation(SET_PRIMARY_USER_EMAIL_MUTATION)
const { mutate: deleteUserEmailMutation } = useMutation(DELETE_USER_EMAIL_MUTATION)
const { mutate: resendUserEmailConfirmation } = useMutation(RESEND_USER_EMAIL_CONFIRMATION_MUTATION)
const { mutate: createTelegramLinkToken } = useMutation(CREATE_TELEGRAM_LINK_TOKEN_MUTATION)
const { mutate: completeTelegramLinkMutation } = useMutation(COMPLETE_TELEGRAM_LINK_MUTATION)
const { mutate: unlinkTelegramAccountMutation } = useMutation(UNLINK_TELEGRAM_ACCOUNT_MUTATION)

// Convert silent hours from UTC to user's timezone for display
function convertSilentHoursFromUtc(silentHours, userTimezone) {
  if (!silentHours || !silentHours.start || !silentHours.end) {
    return { start: null, end: null }
  }

  const tz = userTimezone || 'UTC'

  // Parse UTC times and convert to user's timezone
  const startUtc = parseTimeString(silentHours.start)
  const endUtc = parseTimeString(silentHours.end)

  // Create Date objects for today with UTC times, then convert to user's timezone
  const today = new Date()
  const startDate = new Date(
    Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate(),
      startUtc.hour,
      startUtc.minute
    )
  )
  const endDate = new Date(
    Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate(),
      endUtc.hour,
      endUtc.minute
    )
  )

  // Format in user's timezone
  const startInTz = formatTimeInTimezone(startDate, tz)
  const endInTz = formatTimeInTimezone(endDate, tz)

  return { start: startInTz, end: endInTz }
}

function parseTimeString(timeStr) {
  const parts = timeStr.split(':')
  return { hour: parseInt(parts[0], 10), minute: parseInt(parts[1] || '0', 10) }
}

function formatTimeInTimezone(date, timezone) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date)
}

function clearSilentHours() {
  form.value.silentHoursStartHour = null
  form.value.silentHoursStartMinute = null
  form.value.silentHoursEndHour = null
  form.value.silentHoursEndMinute = null
}

function clearDeadlineReport() {
  form.value.deadlineReportHour = null
  form.value.deadlineReportMinute = null
  form.value.deadlineReportDays = []
}

// Auto-save function for Notification Preferences checkboxes
async function autoSaveNotificationPreferences() {
  if (!userId.value || autoSavingNotifications.value || saving.value) return

  // Only save if form has been initialized (user data loaded)
  if (!user.value) return

  // Don't auto-save if we're currently populating the form
  if (isPopulatingForm.value) return

  autoSavingNotifications.value = true
  notificationsAlert.value = null

  try {
    const { data } = await updatePreferences({
      userId: userId.value,
      dmNotificationsEnabled: form.value.dmNotificationsEnabled,
      emailNotificationsEnabled: form.value.emailNotificationsEnabled,
      notifyOwnActions: form.value.notifyOwnActions,
      notifyApproachingDeadline: form.value.notifyApproachingDeadline,
    })

    if (data?.updateUserPreferences?.errors?.length > 0) {
      notificationsAlert.value = {
        type: 'error',
        title: s__('Profile|Error'),
        message: data.updateUserPreferences.errors.join(', '),
      }
    } else if (data?.updateUserPreferences?.user) {
      const updatedUser = data.updateUserPreferences.user

      // Update form with returned data
      form.value = {
        ...form.value,
        dmNotificationsEnabled: updatedUser.dmNotificationsEnabled,
        emailNotificationsEnabled: updatedUser.emailNotificationsEnabled ?? false,
        notifyOwnActions: updatedUser.notifyOwnActions,
        notifyApproachingDeadline: updatedUser.notifyApproachingDeadline ?? true,
      }

      // Update original form to reflect saved state
      originalForm.value = { ...form.value }

      notificationsAlert.value = {
        type: 'success',
        title: s__('Profile|Success'),
        message: s__('Profile|Preferences saved successfully!'),
      }
    }
  } catch (err) {
    notificationsAlert.value = {
      type: 'error',
      title: s__('Profile|Error'),
      message: err.message || s__('Profile|Failed to save preferences'),
    }
  } finally {
    autoSavingNotifications.value = false
  }
}

// Save function for Silent Hours
async function saveSilentHours() {
  if (!userId.value || savingSilentHours.value || saving.value) return

  savingSilentHours.value = true
  notificationsAlert.value = null

  try {
    // Prepare silent_hours for submission (in user's timezone, backend will convert to UTC)
    const startTime =
      form.value.silentHoursStartHour && form.value.silentHoursStartMinute !== null
        ? `${form.value.silentHoursStartHour}:${form.value.silentHoursStartMinute}`
        : null
    const endTime =
      form.value.silentHoursEndHour && form.value.silentHoursEndMinute !== null
        ? `${form.value.silentHoursEndHour}:${form.value.silentHoursEndMinute}`
        : null

    const silentHours =
      startTime && endTime
        ? {
            start: startTime,
            end: endTime,
          }
        : {}

    const { data } = await updatePreferences({
      userId: userId.value,
      silentHours: silentHours,
    })

    if (data?.updateUserPreferences?.errors?.length > 0) {
      notificationsAlert.value = {
        type: 'error',
        title: s__('Profile|Error'),
        message: data.updateUserPreferences.errors.join(', '),
      }
    } else if (data?.updateUserPreferences?.user) {
      const updatedUser = data.updateUserPreferences.user

      // Convert silent_hours from UTC to user's timezone for display
      const userTz = form.value.preferredTimezone || detectedTimezone.value || 'UTC'
      const silentHoursInUserTz = convertSilentHoursFromUtc(updatedUser.silentHours, userTz)

      // Parse time strings into hour and minute components
      const startParts = silentHoursInUserTz.start
        ? silentHoursInUserTz.start.split(':')
        : [null, null]
      const endParts = silentHoursInUserTz.end ? silentHoursInUserTz.end.split(':') : [null, null]

      // Update form with returned data
      form.value = {
        ...form.value,
        silentHoursStartHour: startParts[0] || null,
        silentHoursStartMinute: startParts[1] || null,
        silentHoursEndHour: endParts[0] || null,
        silentHoursEndMinute: endParts[1] || null,
      }

      // Update original form to reflect saved state
      originalForm.value = { ...form.value }

      notificationsAlert.value = {
        type: 'success',
        title: s__('Profile|Success'),
        message: s__('Profile|Silent hours saved successfully!'),
      }
    }
  } catch (err) {
    notificationsAlert.value = {
      type: 'error',
      title: s__('Profile|Error'),
      message: err.message || s__('Profile|Failed to save silent hours'),
    }
  } finally {
    savingSilentHours.value = false
  }
}

// Handle save from RegularReportsPanel
async function handleSaveDeadlineReport({ hour, minute, days }) {
  if (!userId.value || savingDeadlineReport.value || saving.value) return

  savingDeadlineReport.value = true
  if (regularReportsPanelRef.value) {
    regularReportsPanelRef.value.clearAlert()
  }

  try {
    // Prepare deadline report time for submission
    // Send empty string if time is cleared (nullified) so backend can clear the setting
    const deadlineReportTime =
      hour && minute !== null && minute !== undefined ? `${hour}:${minute}` : ''

    // Prepare deadline report days (convert to integers)
    // Always send the array, even if empty, so the backend can clear the days
    const deadlineReportDays = days ? days.map(d => parseInt(d, 10)) : []

    const { data } = await updatePreferences({
      userId: userId.value,
      deadlineReportTime: deadlineReportTime, // Send empty string to clear, not undefined
      deadlineReportDays: deadlineReportDays,
    })

    if (data?.updateUserPreferences?.errors?.length > 0) {
      if (regularReportsPanelRef.value) {
        regularReportsPanelRef.value.setAlert({
          type: 'error',
          title: s__('Profile|Error'),
          message: data.updateUserPreferences.errors.join(', '),
        })
      }
    } else if (data?.updateUserPreferences?.user) {
      const updatedUser = data.updateUserPreferences.user

      // Parse deadline report time
      const deadlineReportParts = updatedUser.deadlineReportTime
        ? updatedUser.deadlineReportTime.split(':')
        : [null, null]

      // Update form with returned data
      form.value = {
        ...form.value,
        deadlineReportHour: deadlineReportParts[0] || null,
        deadlineReportMinute: deadlineReportParts[1] || null,
        deadlineReportDays: (updatedUser.deadlineReportDays || []).map(d => Number(d)),
      }

      // Update original form to reflect saved state
      originalForm.value = { ...form.value }

      if (regularReportsPanelRef.value) {
        regularReportsPanelRef.value.setAlert({
          type: 'success',
          title: s__('Profile|Success'),
          message: s__('Profile|Deadline report settings saved successfully!'),
        })
      }
    }
  } catch (err) {
    if (regularReportsPanelRef.value) {
      regularReportsPanelRef.value.setAlert({
        type: 'error',
        title: s__('Profile|Error'),
        message: err.message || s__('Profile|Failed to save deadline report settings'),
      })
    }
  } finally {
    savingDeadlineReport.value = false
  }
}

// Auto-save function for Interface Preferences
async function autoSaveInterfacePreferences() {
  if (!userId.value || autoSaving.value || saving.value) return

  // Only save if form has been initialized (user data loaded)
  if (!user.value) return

  // Don't auto-save if we're currently populating the form
  if (isPopulatingForm.value) return

  autoSaving.value = true
  interfaceAlert.value = null

  try {
    const { data } = await updatePreferences({
      userId: userId.value,
      compactTaskForm: form.value.compactTaskForm,
      preferredLanguage: form.value.preferredLanguage,
      preferredTimezone: form.value.preferredTimezone,
      personalLabelsEnabled: form.value.personalLabelsEnabled,
      boardsEnabled: form.value.boardsEnabled,
      templatesEnabled: form.value.templatesEnabled,
      telegramTaskCommandsEnabled: form.value.telegramTaskCommandsEnabled,
    })

    if (data?.updateUserPreferences?.errors?.length > 0) {
      interfaceAlert.value = {
        type: 'error',
        title: s__('Profile|Error'),
        message: data.updateUserPreferences.errors.join(', '),
      }
    } else if (data?.updateUserPreferences?.user) {
      const updatedUser = data.updateUserPreferences.user

      // Update form with returned data
      form.value = {
        ...form.value,
        compactTaskForm: updatedUser.compactTaskForm,
        preferredLanguage: updatedUser.preferredLanguage,
        preferredTimezone: updatedUser.preferredTimezone,
        personalLabelsEnabled: updatedUser.personalLabelsEnabled ?? false,
        boardsEnabled: updatedUser.boardsEnabled ?? true,
        templatesEnabled: updatedUser.templatesEnabled ?? false,
        telegramTaskCommandsEnabled: updatedUser.telegramTaskCommandsEnabled ?? false,
      }

      // Check if language was changed before updating
      const languageChanged = form.value.preferredLanguage !== originalForm.value.preferredLanguage

      // Update original form to reflect saved state
      originalForm.value = { ...form.value }

      interfaceAlert.value = {
        type: 'success',
        title: s__('Profile|Success'),
        message: s__('Profile|Preferences saved successfully!'),
      }

      // If language was changed, reload the page to apply new locale
      if (languageChanged) {
        setTimeout(() => {
          window.location.reload()
        }, 1000)
      }
    }
  } catch (err) {
    interfaceAlert.value = {
      type: 'error',
      title: s__('Profile|Error'),
      message: err.message || s__('Profile|Failed to save preferences'),
    }
  } finally {
    autoSaving.value = false
  }
}

// Track if form has been initialized to prevent auto-save on initial load
const formInitialized = ref(false)
// Track if we're currently populating the form from user data
const isPopulatingForm = ref(false)

// Watch for Interface Preferences changes and auto-save
watch(
  () => [
    form.value.preferredLanguage,
    form.value.preferredTimezone,
    form.value.compactTaskForm,
    form.value.personalLabelsEnabled,
    form.value.boardsEnabled,
    form.value.templatesEnabled,
    form.value.telegramTaskCommandsEnabled,
  ],
  (newValues, oldValues) => {
    // Only auto-save if form has been initialized (user data loaded)
    if (!formInitialized.value) return

    // Don't auto-save if we're currently populating the form
    if (isPopulatingForm.value) return

    // Skip if oldValues is undefined (initial watch)
    if (!oldValues) return

    // Additional safety check: ensure we have a user
    if (!user.value || !userId.value) return

    // Check if any value actually changed
    const hasChanges = newValues.some((val, index) => val !== oldValues[index])
    if (!hasChanges) return

    // Debounce auto-save to avoid too many requests
    if (autoSaveTimeout.value) {
      clearTimeout(autoSaveTimeout.value)
    }
    autoSaveTimeout.value = setTimeout(() => {
      autoSaveInterfacePreferences()
    }, 500)
  },
  { flush: 'post' }
)

const autoSaveTimeout = ref(null)
const notificationAutoSaveTimeout = ref(null)

// Watch for Notification Preferences checkbox changes and auto-save
watch(
  () => [
    form.value.dmNotificationsEnabled,
    form.value.emailNotificationsEnabled,
    form.value.notifyOwnActions,
    form.value.notifyApproachingDeadline,
  ],
  (newValues, oldValues) => {
    // Only auto-save if form has been initialized (user data loaded)
    if (!formInitialized.value) return

    // Don't auto-save if we're currently populating the form
    if (isPopulatingForm.value) return

    // Skip if oldValues is undefined (initial watch)
    if (!oldValues) return

    // Additional safety check: ensure we have a user
    if (!user.value || !userId.value) return

    // Check if any value actually changed
    const hasChanges = newValues.some((val, index) => val !== oldValues[index])
    if (!hasChanges) return

    // Debounce auto-save to avoid too many requests
    if (notificationAutoSaveTimeout.value) {
      clearTimeout(notificationAutoSaveTimeout.value)
    }
    notificationAutoSaveTimeout.value = setTimeout(() => {
      autoSaveNotificationPreferences()
    }, 500)
  },
  { flush: 'post' }
)

// Clear all alerts on component mount to prevent stale alerts
onMounted(() => {
  try {
    const params = new URLSearchParams(window.location.search)
    const sectionFromUrl = params.get(PROFILE_SETTINGS_SECTION_QUERY)
    const emailOptOutAction = params.get(PROFILE_EMAIL_OPT_OUT_QUERY)
    const emailOptOutStatus = params.get(PROFILE_EMAIL_OPT_OUT_STATUS_QUERY)
    if (sectionFromUrl) {
      pendingSettingsSection.value = sectionFromUrl
      params.delete(PROFILE_SETTINGS_SECTION_QUERY)
    } else {
      const storedActiveSection = sessionStorage.getItem(
        PROFILE_SETTINGS_ACTIVE_SECTION_SESSION_KEY
      )
      if (storedActiveSection) {
        pendingSettingsSection.value = storedActiveSection
      }
    }

    if (emailOptOutAction) {
      pendingEmailNotificationsOptOutAction.value = emailOptOutAction
      params.delete(PROFILE_EMAIL_OPT_OUT_QUERY)
    }

    if (emailOptOutStatus) {
      pendingEmailNotificationsOptOutStatus.value = emailOptOutStatus
      params.delete(PROFILE_EMAIL_OPT_OUT_STATUS_QUERY)
    }

    const nextSearch = params.toString()
    const nextUrl =
      window.location.pathname + (nextSearch ? `?${nextSearch}` : '') + window.location.hash
    window.history.replaceState({}, '', nextUrl)

    isSidebarCollapsed.value = readSidebarCollapsedPreference()
  } catch {}

  checkSettingsLayoutCapabilities()
  window.addEventListener('resize', checkSettingsLayoutCapabilities)
  syncActiveProfileSection(profileSidebarItems.value)
  watch(profileSidebarItems, syncActiveProfileSection)

  interfaceAlert.value = null
  accountAlert.value = null
  notificationsAlert.value = null
  applyTelegramLinkStatusFromUrl()
  completeTelegramLinkFromUrl()
  if (regularReportsPanelRef.value) {
    regularReportsPanelRef.value.clearAlert()
  }
})

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', checkSettingsLayoutCapabilities)
  }
})

// handleSubmit and resetForm are no longer needed - all sections now have auto-save or their own Save buttons

// Name editing functions
function startEditName() {
  originalEditingName.value = user.value?.name || ''
  nameForm.value.name = originalEditingName.value
  editingName.value = true
  nameError.value = ''
}

function cancelEditName() {
  nameForm.value.name = originalEditingName.value
  if (user.value && user.value.name !== originalEditingName.value) {
    user.value.name = originalEditingName.value
  }
  editingName.value = false
  nameError.value = ''
}

function startEditUsername() {
  originalEditingUsername.value = user.value?.username || ''
  usernameForm.value.username = originalEditingUsername.value
  editingUsername.value = true
  usernameError.value = ''
}

function cancelEditUsername() {
  usernameForm.value.username = originalEditingUsername.value
  if (user.value && user.value.username !== originalEditingUsername.value) {
    user.value.username = originalEditingUsername.value
  }
  editingUsername.value = false
  usernameError.value = ''
}

async function saveName() {
  if (savingName.value) return

  try {
    savingName.value = true
    nameError.value = ''
    const sanitizedName = (nameForm.value.name || '').trim()
    if (sanitizedName.length < NAME_MIN_LENGTH) {
      nameError.value = sprintf(s__('Profile|is too short (minimum is %{count} characters)'), {
        count: NAME_MIN_LENGTH,
      })
      return
    }

    const { data } = await updateUserName({
      userId: userId.value,
      name: sanitizedName || undefined,
    })

    if (data?.updateUserName?.errors?.length) {
      throw new Error(data.updateUserName.errors.join(', '))
    }

    // Refetch user data to ensure cache is updated with fresh data from server
    await refetchUser()

    editingName.value = false
  } catch (e) {
    nameError.value = e.message || 'Failed to update name'
  } finally {
    savingName.value = false
  }
}

async function saveUsername() {
  if (savingUsername.value) return

  try {
    savingUsername.value = true
    usernameError.value = ''

    const { data } = await updateUserUsername({
      userId: userId.value,
      username: usernameForm.value.username,
    })

    if (data?.updateUserUsername?.errors?.length) {
      throw new Error(data.updateUserUsername.errors.join(', '))
    }

    await refetchUser()
    editingUsername.value = false
  } catch (e) {
    usernameError.value = e.message || s__('Profile|Failed to update username')
  } finally {
    savingUsername.value = false
  }
}

function canDeleteEmail(email) {
  const emails = sortedEmails.value
  if (emails.length <= 1) return false
  if (email.primary) return false
  return true
}

function applyTelegramLinkStatusFromUrl() {
  try {
    const currentUrl = new URL(window.location.href)
    const status = currentUrl.searchParams.get('telegram_link_status')
    if (!status) return

    const messageFromUrl = currentUrl.searchParams.get('telegram_link_message')
    if (status === 'success') {
      accountAlert.value = {
        type: 'success',
        title: s__('Profile|Success'),
        message: s__('Profile|Telegram account linked successfully.'),
      }
      refetchUser()
    } else {
      accountAlert.value = {
        type: 'error',
        title: s__('Profile|Error'),
        message: messageFromUrl || s__('Profile|Failed to link Telegram account'),
      }
    }

    currentUrl.searchParams.delete('telegram_link_status')
    currentUrl.searchParams.delete('telegram_link_message')
    window.history.replaceState({}, '', currentUrl.pathname + currentUrl.search + currentUrl.hash)
  } catch {}
}

function handlePendingEmailNotificationsOptOut() {
  if (!user.value) return

  if (pendingEmailNotificationsOptOutStatus.value === PROFILE_EMAIL_OPT_OUT_INVALID_VALUE) {
    notificationsAlert.value = {
      type: 'error',
      title: s__('Profile|Error'),
      message: s__('Profile|This email opt-out link is invalid or has expired.'),
    }
    pendingEmailNotificationsOptOutStatus.value = null
  }

  if (pendingEmailNotificationsOptOutAction.value === PROFILE_EMAIL_OPT_OUT_CONFIRM_VALUE) {
    showEmailNotificationsOptOutConfirm.value = true
    pendingEmailNotificationsOptOutAction.value = null
  }
}

function extractTelegramLinkTokenFromUrl() {
  try {
    const currentUrl = new URL(window.location.href)
    return currentUrl.searchParams.get('telegram_link_token')
  } catch {
    return null
  }
}

function cleanupTelegramLinkTokenFromUrl() {
  try {
    const currentUrl = new URL(window.location.href)
    currentUrl.searchParams.delete('telegram_link_token')
    window.history.replaceState({}, '', currentUrl.pathname + currentUrl.search + currentUrl.hash)
  } catch {}
}

function extractTelegramInitData() {
  const tg = window.Telegram && window.Telegram.WebApp
  if (tg && tg.initData) {
    return tg.initData
  }

  try {
    const hash = window.location.hash
    if (hash && hash.length > 1) {
      const hashParams = new URLSearchParams(hash.substring(1))
      const tgWebAppData = hashParams.get('tgWebAppData')
      if (tgWebAppData) {
        return decodeURIComponent(tgWebAppData)
      }
    }
  } catch {}

  return null
}

async function completeTelegramLinkFromUrl() {
  const token = extractTelegramLinkTokenFromUrl()
  if (!token) return

  // Nothing to do if this account is already linked.
  if (user.value?.telegramUserId) {
    cleanupTelegramLinkTokenFromUrl()
    return
  }

  const initData = extractTelegramInitData()
  if (!initData) {
    accountAlert.value = {
      type: 'error',
      title: s__('Profile|Error'),
      message: s__('Profile|Open this page from Telegram to complete account linking'),
    }
    cleanupTelegramLinkTokenFromUrl()
    return
  }

  try {
    telegramLinkLoading.value = true
    accountAlert.value = null

    const { data } = await completeTelegramLinkMutation({
      token: token,
      initData: initData,
    })

    const payload = data?.completeTelegramLink
    if (!payload?.success || payload?.errors?.length) {
      throw new Error(payload?.errors?.join(', ') || s__('Profile|Failed to link Telegram account'))
    }

    await refetchUser()
    accountAlert.value = {
      type: 'success',
      title: s__('Profile|Success'),
      message: s__('Profile|Telegram account linked successfully.'),
    }
  } catch (e) {
    accountAlert.value = {
      type: 'error',
      title: s__('Profile|Error'),
      message: e.message || s__('Profile|Failed to link Telegram account'),
    }
  } finally {
    telegramLinkLoading.value = false
    cleanupTelegramLinkTokenFromUrl()
  }
}

async function confirmEmailNotificationsOptOut() {
  if (!userId.value || savingEmailNotificationsOptOut.value) return

  savingEmailNotificationsOptOut.value = true
  showEmailNotificationsOptOutConfirm.value = false
  notificationsAlert.value = null

  try {
    const { data } = await updatePreferences({
      userId: userId.value,
      emailNotificationsEnabled: false,
    })

    if (data?.updateUserPreferences?.errors?.length > 0) {
      throw new Error(data.updateUserPreferences.errors.join(', '))
    }

    const updatedUser = data?.updateUserPreferences?.user
    if (updatedUser) {
      isPopulatingForm.value = true
      form.value = {
        ...form.value,
        dmNotificationsEnabled: updatedUser.dmNotificationsEnabled,
        emailNotificationsEnabled: updatedUser.emailNotificationsEnabled ?? false,
        notifyOwnActions: updatedUser.notifyOwnActions,
        notifyApproachingDeadline: updatedUser.notifyApproachingDeadline ?? true,
      }
      originalForm.value = { ...form.value }
      await nextTick()
      isPopulatingForm.value = false
    }

    notificationsAlert.value = {
      type: 'success',
      title: s__('Profile|Success'),
      message: s__(
        'Profile|You have opted out of email notifications. You can turn them on again at any time.'
      ),
    }
  } catch (err) {
    notificationsAlert.value = {
      type: 'error',
      title: s__('Profile|Error'),
      message: err.message || s__('Profile|Failed to turn off email notifications'),
    }
  } finally {
    savingEmailNotificationsOptOut.value = false
  }
}

async function startTelegramLinking() {
  if (telegramLinkLoading.value || !telegramLinkAvailable.value) return

  try {
    telegramLinkLoading.value = true
    accountAlert.value = null

    const { data } = await createTelegramLinkToken()
    const payload = data?.createTelegramLinkToken
    if (!payload?.success || payload?.errors?.length) {
      throw new Error(payload?.errors?.join(', ') || s__('Profile|Failed to create Telegram link'))
    }
    if (!payload.linkUrl) {
      throw new Error(s__('Profile|Failed to create Telegram link'))
    }

    window.open(payload.linkUrl, '_blank', 'noopener,noreferrer')
    accountAlert.value = {
      type: 'success',
      title: s__('Profile|Continue in Telegram'),
      message: s__(
        'Profile|We opened Telegram. Complete linking there, then return to your Profile.'
      ),
    }
  } catch (e) {
    accountAlert.value = {
      type: 'error',
      title: s__('Profile|Error'),
      message: e.message || s__('Profile|Failed to link Telegram account'),
    }
  } finally {
    telegramLinkLoading.value = false
  }
}

async function confirmUnlinkTelegramAccount() {
  if (!userId.value || unlinkTelegramLoading.value) return

  try {
    unlinkTelegramLoading.value = true
    showUnlinkTelegramConfirm.value = false
    accountAlert.value = null

    const { data } = await unlinkTelegramAccountMutation({
      userId: userId.value,
    })

    if (data?.unlinkTelegramAccount?.errors?.length) {
      throw new Error(data.unlinkTelegramAccount.errors.join(', '))
    }

    await refetchUser()
    accountAlert.value = {
      type: 'success',
      title: s__('Profile|Success'),
      message: s__('Profile|Telegram account unlinked.'),
    }
  } catch (e) {
    accountAlert.value = {
      type: 'error',
      title: s__('Profile|Error'),
      message: e.message || s__('Profile|Failed to unlink Telegram account'),
    }
  } finally {
    unlinkTelegramLoading.value = false
  }
}

async function addEmail() {
  if (emailActionLoading.value) return
  if (!newEmailForm.value.email) return

  try {
    emailActionLoading.value = true
    accountAlert.value = null

    const { data } = await createUserEmail({
      userId: userId.value,
      email: newEmailForm.value.email,
    })

    if (data?.createUserEmail?.errors?.length) {
      throw new Error(data.createUserEmail.errors.join(', '))
    }

    await refetchUser()
    newEmailForm.value.email = ''
    accountAlert.value = {
      type: 'success',
      title: s__('Profile|Confirmation sent'),
      message: s__('Profile|We sent a confirmation email to the new address.'),
    }
  } catch (e) {
    accountAlert.value = {
      type: 'error',
      title: s__('Profile|Error'),
      message: e.message || s__('Profile|Failed to add email'),
    }
  } finally {
    emailActionLoading.value = false
  }
}

async function setPrimaryEmail(emailId) {
  if (emailActionLoading.value) return

  try {
    emailActionLoading.value = true
    accountAlert.value = null

    const { data } = await setPrimaryUserEmail({
      userId: userId.value,
      emailId: emailId,
    })

    if (data?.setPrimaryUserEmail?.errors?.length) {
      throw new Error(data.setPrimaryUserEmail.errors.join(', '))
    }

    await refetchUser()
    accountAlert.value = {
      type: 'success',
      title: s__('Profile|Success'),
      message: s__('Profile|Primary email updated.'),
    }
  } catch (e) {
    accountAlert.value = {
      type: 'error',
      title: s__('Profile|Error'),
      message: e.message || s__('Profile|Failed to update primary email'),
    }
  } finally {
    emailActionLoading.value = false
  }
}

async function deleteEmail(emailId) {
  if (emailActionLoading.value) return

  try {
    emailActionLoading.value = true
    accountAlert.value = null

    const { data } = await deleteUserEmailMutation({
      userId: userId.value,
      emailId: emailId,
    })

    if (!data?.deleteUserEmail?.success || data?.deleteUserEmail?.errors?.length) {
      throw new Error(
        data?.deleteUserEmail?.errors?.join(', ') || s__('Profile|Failed to delete email')
      )
    }

    await refetchUser()
    accountAlert.value = {
      type: 'success',
      title: s__('Profile|Success'),
      message: s__('Profile|Email deleted.'),
    }
  } catch (e) {
    accountAlert.value = {
      type: 'error',
      title: s__('Profile|Error'),
      message: e.message || s__('Profile|Failed to delete email'),
    }
  } finally {
    emailActionLoading.value = false
  }
}

function confirmDeleteEmail(email) {
  if (emailActionLoading.value || !canDeleteEmail(email)) return

  const confirmed = window.confirm(
    s__('Profile|Are you sure you want to delete this email address?')
  )
  if (!confirmed) return

  deleteEmail(email.id)
}

async function resendEmailConfirmation(emailId) {
  if (emailActionLoading.value) return

  try {
    emailActionLoading.value = true
    accountAlert.value = null

    const { data } = await resendUserEmailConfirmation({
      userId: userId.value,
      emailId: emailId,
    })

    if (
      !data?.resendUserEmailConfirmation?.success ||
      data?.resendUserEmailConfirmation?.errors?.length
    ) {
      throw new Error(
        data?.resendUserEmailConfirmation?.errors?.join(', ') ||
          s__('Profile|Failed to resend confirmation email')
      )
    }

    await refetchUser()
    accountAlert.value = {
      type: 'success',
      title: s__('Profile|Confirmation sent'),
      message: s__('Profile|Confirmation email was sent again.'),
    }
  } catch (e) {
    accountAlert.value = {
      type: 'error',
      title: s__('Profile|Error'),
      message: e.message || s__('Profile|Failed to resend confirmation email'),
    }
  } finally {
    emailActionLoading.value = false
  }
}
</script>

<style src="../../styles/dashboard.css"></style>
<style src="../../styles/settings.css"></style>
<style scoped>
.form {
  max-width: 100%;
}

.settings-page-content {
  min-width: 0;
}

.dashboard-page-title {
  margin-bottom: 12px;
}

.mobile-settings-nav-toggle {
  display: none;
}

.card.full-width {
  max-width: 100% !important;
  width: 100%;
  margin: 0;
  border-radius: 0;
}

.help-text {
  margin-left: 26px;
}

.language-select {
  max-width: 300px;
  cursor: pointer;
}

.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
}

.feature-flags-block-title {
  margin: 8px 0 10px;
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

/* Button styles inherit from forms.css */

.btn-icon {
  padding: 4px 8px;
  font-size: 14px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-icon:hover {
  background: #f8f9fa;
  color: #495057;
}

.error {
  color: #d32f2f;
  font-size: 14px;
}

.success {
  color: #2e7d32;
  font-size: 14px;
}

.muted {
  color: #999;
  font-size: 14px;
}

.profile-emails-section {
  display: grid;
  gap: 8px;
}

.profile-email-row {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.profile-email-main {
  min-width: 0;
}

.profile-email-address {
  color: #333;
  font-size: 14px;
  word-break: break-word;
}

.profile-email-meta {
  display: flex;
  gap: 6px;
  margin-top: 4px;
}

.profile-email-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  font-size: 11px;
  padding: 2px 8px;
}

.profile-email-badge.primary {
  background: #dbeafe;
  color: #1d4ed8;
}

.profile-email-badge.confirmed {
  background: #dcfce7;
  color: #166534;
}

.profile-email-badge.unconfirmed {
  background: #fef3c7;
  color: #92400e;
}

.profile-email-actions {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.profile-email-add-row {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.profile-email-input {
  flex: 1;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  min-height: 36px;
  padding: 6px 10px;
}

.profile-email-add-button {
  white-space: nowrap;
}

.profile-telegram-link-section {
  display: grid;
  gap: 8px;
}

.profile-telegram-link-status {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.profile-telegram-badge-id {
  font-size: 12px;
  color: #166534;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 999px;
  padding: 2px 8px;
}

.profile-telegram-link-action {
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

@media (max-width: 640px) {
  .profile-email-row {
    flex-direction: column;
    align-items: stretch;
  }

  .profile-email-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .profile-email-add-button {
    min-width: 40px;
    padding-inline: 10px;
  }

  .profile-telegram-link-action {
    width: 100%;
    padding-right: 12px;
    box-sizing: border-box;
  }
}

@media (max-width: 1023px) {
  .mobile-settings-nav-toggle {
    display: inline-flex;
    position: sticky;
    top: 12px;
    z-index: 20;
    margin-bottom: 10px;
  }
}

.dashboard-link {
  color: #1d6bf3;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.dashboard-link:hover {
  background-color: #f5f5f5;
  text-decoration: none;
}

.setup-link {
  color: #1d6bf3;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  padding: 6px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.setup-link:hover {
  background-color: #f5f5f5;
  text-decoration: none;
}

.deadline-report-section {
  background-color: #f0f7ff;
  border: 1px solid #d0e7ff;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 16px;
}

.deadline-report-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.deadline-report-time-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.deadline-report-days-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.clear-deadline-report-btn {
  padding: 8px 12px;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  color: #666;
  opacity: 0.6;
  transition: all 0.2s;
  align-self: flex-start;
}

.clear-deadline-report-btn.active {
  background: #e3f2fd;
  border-color: #2196f3;
  color: #1976d2;
  opacity: 1;
}

.clear-deadline-report-btn:disabled {
  cursor: not-allowed;
}

.clear-deadline-report-btn:not(:disabled):hover {
  opacity: 1;
}

/* Info block styles */
.info-block {
  margin-top: 12px;
  margin-left: 26px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  overflow: hidden;
}

.info-block-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  cursor: pointer;
  user-select: none;
  transition: background 0.2s;
}

.info-block-header:hover {
  background: #e9ecef;
}

.info-icon {
  font-size: 16px;
}

.info-title {
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  color: #495057;
}

.info-block-content {
  padding: 12px;
  font-size: 13px;
  color: #495057;
  line-height: 1.6;
  border-top: 1px solid #e0e0e0;
}

.info-block-content p {
  margin: 0 0 12px 0;
}

.info-block-content p:last-child {
  margin-bottom: 0;
}

.info-block-content code {
  background: #e9ecef;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  color: #d63384;
}

.info-block-content .commands-list {
  margin: 8px 0 12px 0;
  padding-left: 20px;
}

.info-block-content .commands-list li {
  margin-bottom: 8px;
}

.info-block-content .example-text {
  background: #fff;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #dee2e6;
  margin: 8px 0 12px 0;
}

.info-block-content .note-text {
  background: #fff3cd;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #ffc107;
  color: #856404;
  margin-bottom: 0;
}

@media (max-width: 768px) {
  .profile-name-edit-btn {
    margin-left: auto;
  }

  .compact-task-form-row {
    width: 100%;
    align-items: center;
  }

  .compact-task-form-row .checkbox-label {
    flex: 1;
    width: 100%;
  }
}
</style>
